'use client';

import { useCallback, useEffect, useRef } from 'react';
import Link from 'next/link';
import {
  NAVIGATION_CONTEXTS,
  type NavigationContext,
  type ResolvedNavigationGroup,
  type ResolvedNavigationLeaf,
  type ResolvedNavigationSection,
} from '@/src/lib/navigation';
import type { RoleLevel } from '@/src/lib/navigation';
import { ROLE_LEVEL_LABELS } from '@/src/lib/roles';
import {
  useRecentEntries,
  useFavorites,
  useCollapsedGroups,
  type RecentEntry,
} from '@/src/lib/navigation-recent';
import {
  useWorkflowStep,
  type WorkflowStep,
} from '@/src/lib/sidebar-workflow';
import { InlineHelp } from '@/src/components/ui-kontora/inline-help';
import { SidebarSearch } from './sidebar-search';

function slugify(input: string): string {
  return input
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

const CONTEXT_ACCENTS: Record<
  NavigationContext,
  { ring: string; iconBg: string; iconText: string; chip: string; chipText: string }
> = {
  overview: {
    ring: 'ring-[#1e5b8c]/15',
    iconBg: 'bg-[var(--k-color-context-overview-icon-bg)]',
    iconText: 'text-[var(--k-color-context-overview-text)]',
    chip: 'bg-[var(--k-color-context-overview-icon-bg)]',
    chipText: 'text-[var(--k-color-context-overview-text)]',
  },
  operations: {
    ring: 'ring-[#1e5b8c]/15',
    iconBg: 'bg-[var(--k-color-context-operations-icon-bg)]',
    iconText: 'text-[var(--k-color-context-operations-text)]',
    chip: 'bg-[var(--k-color-context-operations-icon-bg)]',
    chipText: 'text-[var(--k-color-context-operations-text)]',
  },
  accounting: {
    ring: 'ring-[#1f8a8a]/20',
    iconBg: 'bg-[var(--k-color-context-accounting-icon-bg)]',
    iconText: 'text-[var(--k-color-context-accounting-text)]',
    chip: 'bg-[var(--k-color-context-accounting-icon-bg)]',
    chipText: 'text-[var(--k-color-context-accounting-text)]',
  },
  advisor: {
    ring: 'ring-[#6f42c1]/20',
    iconBg: 'bg-[var(--k-color-context-advisor-icon-bg)]',
    iconText: 'text-[var(--k-color-context-advisor-text)]',
    chip: 'bg-[var(--k-color-context-advisor-icon-bg)]',
    chipText: 'text-[var(--k-color-context-advisor-text)]',
  },
  tenant: {
    ring: 'ring-[#c89b3c]/25',
    iconBg: 'bg-[var(--k-color-context-tenant-icon-bg)]',
    iconText: 'text-[var(--k-color-context-tenant-text)]',
    chip: 'bg-[var(--k-color-context-tenant-icon-bg)]',
    chipText: 'text-[var(--k-color-context-tenant-text)]',
  },
  system: {
    ring: 'ring-[#b42318]/20',
    iconBg: 'bg-[var(--k-color-context-system-icon-bg)]',
    iconText: 'text-[var(--k-color-context-system-text)]',
    chip: 'bg-[var(--k-color-context-system-icon-bg)]',
    chipText: 'text-[var(--k-color-context-system-text)]',
  },
};

interface QuickLink {
  id: string;
  label: string;
  href: string;
  context: NavigationContext;
  iconKey: 'dashboard' | 'invoice' | 'partner' | 'report' | 'settings';
}

const QUICK_LINKS: readonly QuickLink[] = [
  { id: 'qa-dashboard', label: 'Dashboard', href: '/dashboard', context: 'overview', iconKey: 'dashboard' },
  { id: 'qa-invoices', label: 'Rechnungen', href: '/verkauf/rechnungen', context: 'operations', iconKey: 'invoice' },
  { id: 'qa-partner', label: 'Partner', href: '/stammdaten/partner', context: 'operations', iconKey: 'partner' },
  { id: 'qa-reports', label: 'Berichte', href: '/reports', context: 'accounting', iconKey: 'report' },
  { id: 'qa-settings', label: 'Einstellungen', href: '/einstellungen/mandant', context: 'tenant', iconKey: 'settings' },
];

const FOCUS_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--k-color-focus-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--k-color-surface-sidebar)]';

const COLLAPSE_THRESHOLD = 4;

export interface AppSidebarProps {
  sections: ResolvedNavigationSection[];
  roleLevel: RoleLevel;
  roles: readonly string[];
  activeContext: NavigationContext;
  powerMode: boolean;
}

export function AppSidebar({
  sections,
  roleLevel,
  roles,
  activeContext,
  powerMode,
}: AppSidebarProps) {
  const workflow = useWorkflowStep();
  const showWorkflow = activeContext === 'accounting';
  const activeGroupLabel = showWorkflow ? workflow.activeStep?.groupLabel ?? null : null;

  const timelineRef = useRef<HTMLDivElement | null>(null);
  const activePillRef = useRef<HTMLButtonElement | null>(null);

  const focusActivePill = useCallback(() => {
    if (activePillRef.current) {
      activePillRef.current.focus({ preventScroll: false });
      activePillRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      return true;
    }
    if (timelineRef.current) {
      timelineRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return true;
    }
    return false;
  }, []);

  useGlobalWorkflowShortcut(showWorkflow, focusActivePill);

  return (
    <aside
      data-tour="sidebar"
      className="sticky top-0 hidden h-dvh w-[18.5rem] shrink-0 flex-col border-r border-[var(--k-color-border-subtle)] bg-[var(--k-color-surface-sidebar)] lg:flex"
    >
      <BrandHeader />
      <RoleSummary roleLevel={roleLevel} roles={roles} />
      <ContextBadge context={activeContext} powerMode={powerMode} />

      <SidebarSearch sections={sections} />

      <div className="min-h-0 flex-1 overflow-y-auto">
        <nav
          aria-label="Hauptnavigation"
          className="space-y-4 px-3 pb-6 pt-1"
        >
          <QuickAccessStrip />
          <QuickJump />
          {showWorkflow ? (
            <WorkflowTimeline
              ref={timelineRef}
              steps={workflow.steps}
              activeStepId={workflow.activeStepId}
              activePillRef={activePillRef}
            />
          ) : null}
          {sections.length === 0 ? (
            <EmptyContext context={activeContext} />
          ) : (
            <div className="space-y-3">
              {sections.map((section, idx) => (
                <SectionBlock
                  key={section.label}
                  section={section}
                  showTopDivider={idx > 0 || showWorkflow}
                  activeGroupLabel={activeGroupLabel}
                />
              ))}
            </div>
          )}
        </nav>
      </div>

      <SidebarFooter activeContext={activeContext} />
    </aside>
  );
}

function BrandHeader() {
  return (
    <div className="border-b border-[var(--k-color-border-subtle)] px-5 py-4">
      <Link
        href="/dashboard"
        className={`flex items-center gap-3 rounded-[var(--k-radius-sm)] ${FOCUS_RING}`}
      >
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--k-radius-md)] bg-[var(--k-color-accent)] text-base font-bold tracking-tight text-white shadow-[var(--k-shadow-sm)]">
          K
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold leading-tight text-[var(--k-color-text-primary)]">
            Kontora
          </p>
          <p className="text-[11px] leading-tight text-[var(--k-color-text-muted)]">
            Buchhaltung klar geführt.
          </p>
        </div>
      </Link>
    </div>
  );
}

function RoleSummary({ roleLevel, roles }: { roleLevel: RoleLevel; roles: readonly string[] }) {
  return (
    <div className="mx-3 mb-2 mt-3 rounded-[var(--k-radius-md)] border border-[var(--k-color-border-subtle)] bg-[var(--k-color-surface-muted)] px-3 py-2.5">
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[var(--k-color-text-muted)]">
        Angemeldet als
      </p>
      <p className="mt-0.5 text-[13px] font-semibold text-[var(--k-color-text-primary)]">
        {ROLE_LEVEL_LABELS[roleLevel]}
      </p>
      {roles.length > 0 ? (
        <p className="mt-0.5 text-[11px] leading-snug text-[var(--k-color-text-muted)]">
          {roles.join(' · ')}
        </p>
      ) : null}
    </div>
  );
}

function ContextBadge({
  context,
  powerMode,
}: {
  context: NavigationContext;
  powerMode: boolean;
}) {
  const meta = NAVIGATION_CONTEXTS[context];
  const accent = CONTEXT_ACCENTS[context];
  return (
    <div className="mx-3 mb-2">
      <div
        className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] ring-1 ${accent.chip} ${accent.chipText} ${accent.ring}`}
      >
        <span aria-hidden="true" className="text-[11px]">
          {meta.icon}
        </span>
        {meta.label}
      </div>
      <p className="mt-1 text-[11px] leading-snug text-[var(--k-color-text-muted)]">
        {meta.description}
      </p>
      {powerMode ? (
        <p className="mt-1.5 inline-flex items-center gap-1 rounded-md bg-[var(--k-color-area-system-soft)] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[var(--k-color-role-system-text)]">
          <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-[var(--k-color-role-system-icon)]" />
          Power-Mode aktiv
          <InlineHelp
            term="Power-Mode"
            description="Aktuell sichtbar: alle Bereiche unabhängig von Berechtigungen. Aktionen werden weiterhin auditiert."
          />
        </p>
      ) : null}
    </div>
  );
}

function EmptyContext({ context }: { context: NavigationContext }) {
  const meta = NAVIGATION_CONTEXTS[context];
  return (
    <div className="mx-1 rounded-[var(--k-radius-md)] border border-dashed border-[var(--k-color-border-default)] bg-[var(--k-color-surface-muted)] p-3 text-center">
      <p className="text-[13px] font-semibold text-[var(--k-color-text-primary)]">
        Keine Einträge in „{meta.label}“
      </p>
      <p className="mt-1 text-[11px] leading-snug text-[var(--k-color-text-muted)]">
        Für deine aktuelle Rolle sind in diesem Bereich keine Berechtigungen freigeschaltet.
      </p>
    </div>
  );
}

function QuickAccessStrip() {
  return (
    <section
      aria-label="Schnellzugriff"
      data-tour="quick-access"
      className="rounded-[var(--k-radius-md)] border border-[var(--k-color-border-subtle)] bg-[var(--k-color-surface)] px-2 py-2"
    >
      <p className="px-1 pb-1 text-[10px] font-bold uppercase tracking-[0.16em] text-[var(--k-color-text-muted)]">
        Schnellzugriff
      </p>
      <ul className="flex items-stretch justify-between gap-1">
        {QUICK_LINKS.map((link) => (
          <li key={link.id} className="flex-1">
            <Link
              href={link.href}
              title={link.label}
              aria-label={link.label}
              data-tour={`quick-${link.id}`}
              className={`flex h-9 w-full items-center justify-center rounded-[var(--k-radius-sm)] text-[var(--k-color-text-secondary)] transition duration-150 hover:bg-[var(--k-color-accent-soft)] hover:text-[var(--k-color-text-link)] ${FOCUS_RING}`}
            >
              <QuickIcon iconKey={link.iconKey} />
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

function QuickIcon({ iconKey }: { iconKey: QuickLink['iconKey'] }) {
  const common = 'h-4 w-4';
  switch (iconKey) {
    case 'dashboard':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className={common}
        >
          <rect x="3" y="3" width="7" height="9" rx="1.2" />
          <rect x="14" y="3" width="7" height="5" rx="1.2" />
          <rect x="14" y="12" width="7" height="9" rx="1.2" />
          <rect x="3" y="16" width="7" height="5" rx="1.2" />
        </svg>
      );
    case 'invoice':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className={common}
        >
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="9" y1="13" x2="15" y2="13" />
          <line x1="9" y1="17" x2="13" y2="17" />
        </svg>
      );
    case 'partner':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className={common}
        >
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      );
    case 'report':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className={common}
        >
          <line x1="18" y1="20" x2="18" y2="10" />
          <line x1="12" y1="20" x2="12" y2="4" />
          <line x1="6" y1="20" x2="6" y2="14" />
        </svg>
      );
    case 'settings':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className={common}
        >
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h0a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
      );
  }
}

function QuickJump({ recent }: { recent?: RecentEntry[] }) {
  const { entries } = useRecentEntries();
  const { favorites, toggle } = useFavorites();

  const visibleRecent = (entries.length > 0 ? entries : recent ?? []).slice(0, 5);

  if (visibleRecent.length === 0 && favorites.length === 0) return null;

  return (
    <div className="space-y-2">
      {favorites.length > 0 ? (
        <div className="rounded-[var(--k-radius-md)] border border-amber-300/40 bg-amber-50/40 px-2 py-1.5">
          <p className="flex items-center justify-between px-1 text-[10px] font-bold uppercase tracking-[0.16em] text-[var(--k-color-warning-strong)]">
            <span>Favoriten</span>
            <span className="rounded-full bg-amber-200/60 px-1.5 py-0.5 text-[9px] font-semibold normal-case tracking-normal text-[var(--k-color-warning-strong)]">
              {favorites.length}
            </span>
          </p>
          <ul className="mt-1 space-y-0.5">
            {favorites.slice(0, 4).map((favId) => {
              const [, ...rest] = favId.split('::');
              const href = rest.join('::');
              const label = href.split('?')[0]?.split('/').filter(Boolean).pop() ?? href;
              return (
                <li key={favId} className="flex items-center gap-1">
                  <Link
                    href={href}
                    className={`flex flex-1 items-center gap-2 truncate rounded-[var(--k-radius-sm)] px-2 py-1 text-[12px] font-medium text-[var(--k-color-text-secondary)] transition duration-150 hover:bg-amber-100/60 hover:text-[var(--k-color-text-primary)] ${FOCUS_RING}`}
                    title={href}
                  >
                    <span aria-hidden="true" className="text-[11px] text-[var(--k-color-warning-strong)]">★</span>
                    <span className="truncate">{label.replace(/-/g, ' ')}</span>
                  </Link>
                  <button
                    type="button"
                    onClick={() => toggle(favId)}
                    aria-label="Favorit entfernen"
                    className={`rounded p-1 text-[10px] text-[var(--k-color-warning-strong)] transition hover:bg-amber-100 ${FOCUS_RING}`}
                  >×</button>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      {visibleRecent.length > 0 ? (
        <div>
          <p className="flex items-center justify-between px-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-[var(--k-color-text-muted)]">
            <span>Zuletzt verwendet</span>
            <span className="rounded-full bg-[var(--k-color-surface-muted)] px-1.5 py-0.5 text-[9px] font-semibold normal-case tracking-normal">
              {visibleRecent.length}
            </span>
          </p>
          <ul className="mt-1 space-y-0.5">
            {visibleRecent.map((entry) => (
              <li key={`${entry.href}-${entry.visitedAt}`}>
                <Link
                  href={entry.href}
                  className={`flex items-center gap-2 truncate rounded-[var(--k-radius-sm)] px-2 py-1 text-[12px] font-medium text-[var(--k-color-text-secondary)] transition duration-150 hover:bg-[var(--k-color-surface-sidebar-hover)] hover:text-[var(--k-color-text-primary)] ${FOCUS_RING}`}
                  title={entry.label}
                >
                  <span
                    aria-hidden="true"
                    className={`flex h-4 w-4 shrink-0 items-center justify-center rounded text-[8px] font-bold ${CONTEXT_ACCENTS[entry.context as NavigationContext]?.iconBg ?? 'bg-[var(--k-color-surface-muted)]'} ${CONTEXT_ACCENTS[entry.context as NavigationContext]?.iconText ?? 'text-[var(--k-color-text-muted)]'}`}
                  >
                    {NAVIGATION_CONTEXTS[entry.context as NavigationContext]?.icon ?? '?'}
                  </span>
                  <span className="truncate">{entry.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function SectionBlock({
  section,
  showTopDivider,
  activeGroupLabel,
}: {
  section: ResolvedNavigationSection;
  showTopDivider: boolean;
  activeGroupLabel?: string | null;
}) {
  const accent = CONTEXT_ACCENTS[section.context];
  const visibleGroups = section.groups.filter((g) => g.items.length > 0);
  if (visibleGroups.length === 0) return null;

  return (
    <section
      aria-label={section.label}
      className={`pt-2 first:pt-0 ${showTopDivider ? 'border-t border-[var(--k-color-border-subtle)]' : ''}`}
    >
      <div className="mb-1 flex items-center gap-2 px-1.5">
        <span
          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-[var(--k-radius-sm)] text-[10px] font-bold ${accent.iconBg} ${accent.iconText}`}
          aria-hidden="true"
        >
          {section.icon}
        </span>
        <h2 className="text-[10px] font-bold uppercase tracking-[0.18em] text-[var(--k-color-text-muted)]">
          {section.label}
        </h2>
      </div>
      <div className="space-y-1.5">
        {visibleGroups.map((group) => (
          <GroupBlock
            key={group.label}
            group={group}
            accent={accent}
            isActiveGroup={Boolean(activeGroupLabel && group.label === activeGroupLabel)}
          />
        ))}
      </div>
    </section>
  );
}

function GroupBlock({
  group,
  accent,
  isActiveGroup = false,
}: {
  group: ResolvedNavigationGroup;
  accent: (typeof CONTEXT_ACCENTS)[NavigationContext];
  isActiveGroup?: boolean;
}) {
  const { collapsed, toggle } = useCollapsedGroups();
  const totalItems = group.items.length;
  const isOverflow = totalItems > COLLAPSE_THRESHOLD;
  const containsActive = group.items.some((i) => i.isActive) || isActiveGroup;
  const userExpanded = collapsed.has(group.label);
  const showAll = !isOverflow || userExpanded || containsActive;
  const visibleItems = showAll ? group.items : group.items.slice(0, COLLAPSE_THRESHOLD);
  const hiddenCount = totalItems - COLLAPSE_THRESHOLD;

  return (
    <div
      id={`workflow-group-${slugify(group.label)}`}
      data-tour={`group-${slugify(group.label)}`}
      className="rounded-[var(--k-radius-sm)]"
    >
      <div className="flex items-center justify-between gap-1 px-1">
        <h3 className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-[var(--k-color-text-faint)]">
          {isActiveGroup ? (
            <span
              aria-hidden="true"
              className="inline-block h-1.5 w-1.5 rounded-full bg-[var(--k-color-accent)]"
            />
          ) : null}
          {group.label}
        </h3>
        {isOverflow ? (
          <button
            type="button"
            onClick={() => toggle(group.label)}
            aria-expanded={showAll}
            aria-label={
              showAll
                ? `${group.label} einklappen`
                : `${group.label}: ${hiddenCount} weitere anzeigen`
            }
            title={showAll ? 'Weniger anzeigen' : `+${hiddenCount} weitere`}
            className={`flex h-5 w-5 items-center justify-center rounded text-[var(--k-color-text-muted)] transition duration-150 hover:bg-[var(--k-color-surface-muted)] hover:text-[var(--k-color-text-primary)] ${FOCUS_RING}`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              className={`transition-transform duration-150 ${showAll ? 'rotate-180' : ''}`}
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
        ) : null}
      </div>
      {group.description && showAll ? (
        <p className="mt-0.5 px-2 text-[10px] leading-snug text-[var(--k-color-text-faint)]">
          {group.description}
        </p>
      ) : null}
      <ul className="mt-0.5 space-y-0.5">
        {visibleItems.map((item) => (
          <ItemRow key={item.label} item={item} accent={accent} />
        ))}
      </ul>
      {isOverflow && !showAll ? (
        <button
          type="button"
          onClick={() => toggle(group.label)}
          aria-expanded={false}
          className={`mt-0.5 flex w-full items-center justify-center gap-1 rounded-[var(--k-radius-sm)] px-2 py-1 text-[11px] font-medium text-[var(--k-color-text-muted)] transition duration-150 hover:bg-[var(--k-color-surface-muted)] hover:text-[var(--k-color-text-link)] ${FOCUS_RING}`}
        >
          <span>Mehr anzeigen</span>
          <span className="rounded-full bg-[var(--k-color-bg-canvas)] px-1.5 py-0.5 text-[9px] font-bold text-[var(--k-color-text-muted)]">
            +{hiddenCount}
          </span>
        </button>
      ) : null}
    </div>
  );
}

function ItemRow({
  item,
  accent,
}: {
  item: ResolvedNavigationLeaf;
  accent: (typeof CONTEXT_ACCENTS)[NavigationContext];
}) {
  const initial = item.label.charAt(0).toUpperCase();
  const chipClasses = item.isActive
    ? `${accent.iconBg} ${accent.iconText}`
    : 'bg-[var(--k-color-bg-canvas)] text-[var(--k-color-text-muted)]';

  if (item.href && !item.isDisabled) {
    return (
      <li>
        <Link
          href={item.href}
          aria-current={item.isActive ? 'page' : undefined}
          title={item.description}
          className={`flex items-center gap-2 rounded-[var(--k-radius-sm)] px-2 py-1 text-[13px] font-medium transition ${
            item.isActive
              ? 'bg-[var(--k-color-surface-sidebar-active)] text-[var(--k-color-text-link)]'
              : 'text-[var(--k-color-text-secondary)] hover:bg-[var(--k-color-surface-sidebar-hover)] hover:text-[var(--k-color-text-primary)]'
          } ${FOCUS_RING}`}
        >
          <span
            aria-hidden="true"
            className={`flex h-4 w-4 shrink-0 items-center justify-center rounded text-[9px] font-bold ${chipClasses}`}
          >
            {initial}
          </span>
          <span className="min-w-0 flex-1 truncate">{item.label}</span>
          <span className="flex shrink-0 items-center gap-1.5">
            {item.isComingSoon ? (
              <span className="rounded-full bg-[var(--k-color-bg-canvas)] px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-[var(--k-color-text-muted)]">
                Neu
              </span>
            ) : null}
            {item.isActive ? (
              <span
                aria-hidden="true"
                className="h-1.5 w-1.5 rounded-full bg-[var(--k-color-accent)]"
              />
            ) : null}
          </span>
        </Link>
      </li>
    );
  }

  return (
    <li>
      <span
        title={item.disabledReason}
        className="flex items-center gap-2 rounded-[var(--k-radius-sm)] px-2 py-1 text-[13px] font-medium text-[var(--k-color-text-faint)]"
      >
        <span
          aria-hidden="true"
          className="flex h-4 w-4 shrink-0 items-center justify-center rounded bg-[var(--k-color-bg-canvas)] text-[9px] font-bold text-[var(--k-color-text-muted)]"
        >
          {initial}
        </span>
        <span className="min-w-0 flex-1 truncate">{item.label}</span>
        <span className="rounded-full bg-[var(--k-color-bg-canvas)] px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-[var(--k-color-text-muted)]">
          {item.isComingSoon ? 'In Vorbereitung' : 'Gesperrt'}
        </span>
      </span>
    </li>
  );
}

function SidebarFooter({ activeContext }: { activeContext: NavigationContext }) {
  const { entries: recent, clear } = useRecentEntries();
  const { favorites } = useFavorites();

  return (
    <div className="shrink-0 border-t border-[var(--k-color-border-subtle)] bg-[var(--k-color-surface-muted)] px-3 py-2">
      <p className="flex items-center justify-between text-[10px] font-bold uppercase tracking-[0.16em] text-[var(--k-color-text-muted)]">
        <span>Status</span>
      </p>
      <div className="mt-1 flex items-center justify-between gap-2 text-[10px] text-[var(--k-color-text-faint)]">
        <span className="flex items-center gap-1.5">
          <span className="rounded bg-[var(--k-color-surface)] px-1.5 py-0.5 font-medium">
            {recent.length}
          </span>
          zuletzt
        </span>
        <span className="flex items-center gap-1.5">
          <span className="rounded bg-[var(--k-color-surface)] px-1.5 py-0.5 font-medium">
            {favorites.length}
          </span>
          Favoriten
        </span>
      </div>
      {recent.length > 0 || favorites.length > 0 ? (
        <div className="mt-1 flex items-center justify-between gap-1">
          {recent.length > 0 ? (
            <button
              type="button"
              onClick={() => clear()}
              className={`text-[10px] font-medium text-[var(--k-color-text-muted)] underline-offset-2 hover:text-[var(--k-color-text-primary)] hover:underline ${FOCUS_RING}`}
            >
              Verlauf leeren
            </button>
          ) : <span />}
          <p className="text-[9px] uppercase tracking-wider text-[var(--k-color-text-faint)]">
            {NAVIGATION_CONTEXTS[activeContext]?.shortLabel ?? activeContext}
          </p>
        </div>
      ) : null}
    </div>
  );
}

function WorkflowTimeline({
  ref,
  steps,
  activeStepId,
  activePillRef,
}: {
  ref?: React.RefObject<HTMLDivElement | null>;
  steps: readonly WorkflowStep[];
  activeStepId: WorkflowStep['id'] | null;
  activePillRef?: React.RefObject<HTMLButtonElement | null>;
}) {
  return (
    <section
      ref={ref}
      aria-label="Buchhaltungs-Workflow"
      data-tour="workflow-timeline"
      className="rounded-[var(--k-radius-md)] border border-[var(--k-color-border-subtle)] bg-[var(--k-color-surface)] px-2 py-2"
    >
      <div className="mb-1.5 flex items-center justify-between px-1">
        <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-[var(--k-color-text-muted)]">
          Workflow
        </p>
        {activeStepId ? (
          <span className="rounded-full bg-[var(--k-color-accent-soft)] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-[var(--k-color-text-link)]">
            Stufe {steps.findIndex((s) => s.id === activeStepId) + 1} / {steps.length}
          </span>
        ) : null}
      </div>
      <ol
        role="list"
        className="flex flex-wrap items-center gap-1"
        aria-label="Workflow-Schritte"
      >
        {steps.map((step, idx) => {
          const isActive = step.id === activeStepId;
          return (
            <li key={step.id} className="flex items-center">
              <button
                ref={isActive ? activePillRef : undefined}
                type="button"
                aria-current={isActive ? 'step' : undefined}
                aria-label={`Workflow-Schritt ${idx + 1}: ${step.label}`}
                title={`${step.label} – ${step.description}`}
                data-tour={`workflow-step-${step.id}`}
                onClick={() => {
                  const target = document.getElementById(`workflow-group-${step.id}`);
                  if (target) {
                    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    target.focus({ preventScroll: true });
                  }
                }}
                className={[
                  'group inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--k-color-focus-ring)] focus-visible:ring-offset-1 focus-visible:ring-offset-[var(--k-color-surface-sidebar)]',
                  isActive
                    ? 'bg-[var(--k-color-accent-soft)] text-[var(--k-color-text-link)] ring-1 ring-[var(--k-color-accent)]/40'
                    : 'bg-[var(--k-color-surface-muted)] text-[var(--k-color-text-secondary)] hover:bg-[var(--k-color-accent-soft)] hover:text-[var(--k-color-text-link)]',
                ].join(' ')}
              >
                <span
                  aria-hidden="true"
                  className={[
                    'flex h-4 w-4 items-center justify-center rounded-full text-[9px] font-bold',
                    isActive
                      ? 'bg-[var(--k-color-accent)] text-white'
                      : 'bg-[var(--k-color-surface)] text-[var(--k-color-text-muted)] group-hover:bg-[var(--k-color-surface)]',
                  ].join(' ')}
                >
                  <WorkflowIcon iconKey={step.iconKey} />
                </span>
                <span className="whitespace-nowrap">{step.label}</span>
              </button>
              {idx < steps.length - 1 ? (
                <span
                  aria-hidden="true"
                  className="mx-0.5 h-px w-2 bg-[var(--k-color-border-default)]"
                />
              ) : null}
            </li>
          );
        })}
      </ol>
      <p className="mt-1.5 px-1 text-[10px] leading-snug text-[var(--k-color-text-faint)]">
        {activeStepId
          ? `Aktive Stufe: ${steps.find((s) => s.id === activeStepId)?.label.toUpperCase()}`
          : 'Wähle einen Schritt, um direkt zur passenden Gruppe zu springen.'}
      </p>
    </section>
  );
}

function WorkflowIcon({
  iconKey,
}: {
  iconKey: WorkflowStep['iconKey'];
}) {
  const common = 'h-2.5 w-2.5';
  switch (iconKey) {
    case 'inbox':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className={common}
        >
          <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
          <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
        </svg>
      );
    case 'arrows':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className={common}
        >
          <polyline points="17 1 21 5 17 9" />
          <path d="M3 11V9a4 4 0 0 1 4-4h14" />
          <polyline points="7 23 3 19 7 15" />
          <path d="M21 13v2a4 4 0 0 1-4 4H3" />
        </svg>
      );
    case 'book':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className={common}
        >
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
        </svg>
      );
    case 'percent':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className={common}
        >
          <line x1="19" y1="5" x2="5" y2="19" />
          <circle cx="6.5" cy="6.5" r="2.5" />
          <circle cx="17.5" cy="17.5" r="2.5" />
        </svg>
      );
    case 'chart':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className={common}
        >
          <line x1="18" y1="20" x2="18" y2="10" />
          <line x1="12" y1="20" x2="12" y2="4" />
          <line x1="6" y1="20" x2="6" y2="14" />
        </svg>
      );
    case 'check':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className={common}
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      );
  }
}

function useGlobalWorkflowShortcut(
  enabled: boolean,
  onTrigger: () => boolean,
): void {
  const handlerRef = useRef(onTrigger);

  useEffect(() => {
    handlerRef.current = onTrigger;
  }, [onTrigger]);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;
    let pendingG = false;
    let pendingTimer: ReturnType<typeof setTimeout> | null = null;

    const clearPending = () => {
      pendingG = false;
      if (pendingTimer) {
        clearTimeout(pendingTimer);
        pendingTimer = null;
      }
    };

    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (
          tag === 'INPUT' ||
          tag === 'TEXTAREA' ||
          tag === 'SELECT' ||
          target.isContentEditable
        ) {
          return;
        }
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const key = e.key.toLowerCase();
      if (key === 'g') {
        pendingG = true;
        if (pendingTimer) clearTimeout(pendingTimer);
        pendingTimer = setTimeout(clearPending, 900);
        return;
      }
      if (pendingG && key === 'w') {
        clearPending();
        const handled = handlerRef.current();
        if (handled) {
          e.preventDefault();
        }
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      if (pendingTimer) clearTimeout(pendingTimer);
    };
  }, [enabled]);
}
